import React, { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTIES = ['Agder','Akershus','Buskerud','Finnmark','Innlandet','Møre og Romsdal','Nordland','Oslo','Rogaland','Telemark','Troms','Trøndelag','Vestfold','Vestland','Østfold'];

function formatActivityLog(d) {
  if (!d.last_modified_at) return null;
  try {
    const date = new Date(d.last_modified_at).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const who = d.last_modified_by || 'admin';
    const action = d.last_modified_action || 'endret';
    return `Endret av ${who} ${date} (${action})`;
  } catch {
    return null;
  }
}

export default function DealersTab({ dealers, loading, auth, loadDealers }) {
  const [resetModal, setResetModal] = useState(null);
  const [profileModal, setProfileModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [timelineModal, setTimelineModal] = useState(null); // { dealer, entries, loading }
  const [undoState, setUndoState] = useState(null); // { snapshotId, dealerName, secondsLeft }
  const [newPassword, setNewPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [profileData, setProfileData] = useState({});
  const [profileMsg, setProfileMsg] = useState('');
  const [deleteMsg, setDeleteMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteWarranties, setDeleteWarranties] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Countdown the undo toast; auto-dismiss after 30 seconds
  React.useEffect(() => {
    if (!undoState) return undefined;
    if (undoState.secondsLeft <= 0) {
      setUndoState(null);
      return undefined;
    }
    const t = setTimeout(() => setUndoState({ ...undoState, secondsLeft: undoState.secondsLeft - 1 }), 1000);
    return () => clearTimeout(t);
  }, [undoState]);

  const openTimeline = async (d) => {
    setTimelineModal({ dealer: d, entries: [], loading: true });
    try {
      const res = await fetch(`${API}/admin/dealers/${d.id}/activity-log`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) {
        const entries = await res.json();
        setTimelineModal({ dealer: d, entries, loading: false });
      } else {
        setTimelineModal({ dealer: d, entries: [], loading: false });
      }
    } catch {
      setTimelineModal({ dealer: d, entries: [], loading: false });
    }
  };

  const restoreDealer = async () => {
    if (!undoState) return;
    try {
      const res = await fetch(`${API}/admin/dealers/restore/${undoState.snapshotId}`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}` },
      });
      if (res.ok) {
        setUndoState(null);
        if (loadDealers) loadDealers();
      }
    } catch { /* ignore */ }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 4) { setResetMsg('Passord må være minst 4 tegn'); return; }
    try {
      const res = await fetch(`${API}/admin/dealers/${resetModal.id}/reset-password`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });
      if (res.ok) { setResetMsg('Passord endret!'); setNewPassword(''); if (loadDealers) loadDealers(); }
      else { const err = await res.json(); setResetMsg(err.detail || 'Kunne ikke endre passord.'); }
    } catch { setResetMsg('Kunne ikke koble til serveren.'); }
  };

  const resetPin = async () => {
    if (!newPin || newPin.length !== 4 || !/^\d{4}$/.test(newPin)) { setResetMsg('PIN må være 4 siffer'); return; }
    try {
      const res = await fetch(`${API}/admin/dealers/${resetModal.id}/reset-pin`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_pin: newPin })
      });
      if (res.ok) { setResetMsg('PIN endret!'); setNewPin(''); if (loadDealers) loadDealers(); }
      else { const err = await res.json(); setResetMsg(err.detail || 'Kunne ikke endre PIN.'); }
    } catch { setResetMsg('Kunne ikke koble til serveren.'); }
  };

  const openProfile = (d) => {
    setProfileModal(d);
    setProfileData({
      company_name: d.company_name || '',
      contact_person: d.contact_person || '',
      email: d.email || '',
      phone: d.phone || '',
      website_url: d.website_url || '',
      description: d.description || '',
      logo_url: d.logo_url || '',
      county: d.county || '',
      public_phone: d.public_phone || d.phone || '',
      public_email: d.public_email || d.email || '',
      is_public: d.is_public === true,
      is_nationwide: d.is_nationwide === true || d.nationwide === true,
    });
    setProfileMsg('');
  };

  const saveProfile = async () => {
    try {
      const res = await fetch(`${API}/admin/dealers/${profileModal.id}/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      if (res.ok) {
        setProfileMsg('Profil oppdatert!');
        setTimeout(() => {
          if (loadDealers) loadDealers();
          setProfileModal(null);
        }, 800);
      }
      else { const err = await res.json(); setProfileMsg(err.detail || 'Kunne ikke oppdatere profilen.'); }
    } catch { setProfileMsg('Kunne ikke koble til serveren.'); }
  };

  const openDelete = (d) => {
    setDeleteModal(d);
    setDeleteMsg('');
    setDeleteConfirm('');
    setDeleteWarranties(false);
  };

  const confirmDelete = async () => {
    if (deleteConfirm !== deleteModal.company_name) {
      setDeleteMsg(`Skriv "${deleteModal.company_name}" nøyaktig for å bekrefte.`);
      return;
    }
    setDeleting(true);
    try {
      const url = `${API}/admin/dealers/${deleteModal.id}${deleteWarranties ? '?delete_warranties=true' : ''}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDeleteMsg('Forhandler slettet!');
        setTimeout(() => {
          if (loadDealers) loadDealers();
          setDeleteModal(null);
          if (data.snapshot_id) {
            setUndoState({ snapshotId: data.snapshot_id, dealerName: deleteModal.company_name, secondsLeft: 30 });
          }
        }, 400);
      } else {
        const err = await res.json();
        setDeleteMsg(err.detail || 'Kunne ikke slette forhandleren.');
      }
    } catch { setDeleteMsg('Kunne ikke koble til serveren.'); }
    setDeleting(false);
  };

  if (loading) return <div className="text-stone-500">Laster...</div>;
  return (
    <div>
      <h2 className="text-2xl font-semibold text-stone-900 mb-6">Forhandlere ({dealers.length})</h2>
      {dealers.length === 0 ? (
        <p className="text-stone-500">Ingen forhandlere registrert.</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-stone-200" data-testid="dealers-table">
            <thead className="bg-stone-50">
              <tr>
                {['Firma', 'Kontaktperson', 'E-post', 'Telefon', 'Brukernavn', 'Status', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {dealers.map((d) => {
                const activity = formatActivityLog(d);
                return (
                  <tr key={d.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 text-sm font-medium text-stone-900">
                      {d.company_name}
                      {activity && (
                        <button
                          type="button"
                          onClick={() => openTimeline(d)}
                          className="block text-xs font-normal text-stone-400 hover:text-emerald-800 hover:underline mt-0.5 text-left"
                          data-testid={`activity-log-${d.id}`}
                          title="Vis full endringslogg"
                        >
                          {activity}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">{d.contact_person}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{d.email}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{d.phone}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{d.username}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${d.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{d.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => openProfile(d)} className="text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2 py-1 rounded-lg transition-colors" data-testid={`profile-btn-${d.id}`}>
                          Rediger
                        </button>
                        <button onClick={() => { setResetModal(d); setResetMsg(''); setNewPassword(''); setNewPin(''); }} className="text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-2 py-1 rounded-lg transition-colors" data-testid={`reset-btn-${d.id}`}>
                          Resett
                        </button>
                        <button onClick={() => openDelete(d)} className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded-lg transition-colors" data-testid={`delete-btn-${d.id}`}>
                          Slett
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resetModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="reset-modal">
            <h3 className="text-lg font-semibold text-stone-900 mb-1">Resett tilgang</h3>
            <p className="text-sm text-stone-500 mb-4">{resetModal.company_name} ({resetModal.username})</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nytt passord</label>
                <div className="flex gap-2">
                  <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Skriv nytt passord" className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-800 focus:border-transparent" />
                  <button onClick={resetPassword} className="bg-emerald-800 text-white text-sm px-4 py-2 rounded-lg hover:bg-emerald-900 transition-colors" data-testid="reset-password-btn">Endre</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Ny PIN-kode (4 siffer)</label>
                <div className="flex gap-2">
                  <input type="text" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="0000" maxLength={4} className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-800 focus:border-transparent" />
                  <button onClick={resetPin} className="bg-emerald-800 text-white text-sm px-4 py-2 rounded-lg hover:bg-emerald-900 transition-colors" data-testid="reset-pin-btn">Endre</button>
                </div>
              </div>
            </div>
            {resetMsg && <p className={`text-sm mt-3 ${resetMsg.includes('endret') ? 'text-emerald-600' : 'text-red-600'}`}>{resetMsg}</p>}
            <button onClick={() => setResetModal(null)} className="w-full mt-4 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 font-medium transition-colors">Lukk</button>
          </div>
        </div>
      )}

      {profileModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="profile-modal">
            <h3 className="text-lg font-semibold text-stone-900 mb-1">Rediger forhandler</h3>
            <p className="text-sm text-stone-500 mb-4">{profileModal.company_name} - profil vises på forhandlersiden</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Firmanavn</label>
                <input type="text" value={profileData.company_name} onChange={(e) => setProfileData({...profileData, company_name: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-company-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Kontaktperson</label>
                <input type="text" value={profileData.contact_person} onChange={(e) => setProfileData({...profileData, contact_person: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-contact-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">E-post (konto)</label>
                  <input type="email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-account-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Telefon (konto)</label>
                  <input type="text" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-account-phone-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 px-3 py-2 border border-stone-300 rounded-lg cursor-pointer hover:bg-stone-50">
                  <input type="checkbox" checked={!!profileData.is_public} onChange={(e) => setProfileData({...profileData, is_public: e.target.checked})} data-testid="profile-is-public-checkbox" />
                  <span className="text-sm text-stone-700">Vis på kart</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 border border-stone-300 rounded-lg cursor-pointer hover:bg-stone-50">
                  <input type="checkbox" checked={!!profileData.is_nationwide} onChange={(e) => setProfileData({...profileData, is_nationwide: e.target.checked})} data-testid="profile-is-nationwide-checkbox" />
                  <span className="text-sm text-stone-700">Landsdekkende</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Fylke</label>
                <select value={profileData.county} onChange={(e) => setProfileData({...profileData, county: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-county-select">
                  <option value="">Velg fylke...</option>
                  {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Beskrivelse</label>
                <textarea value={profileData.description} onChange={(e) => setProfileData({...profileData, description: e.target.value})} rows={3} placeholder="Kort presentasjon av forhandleren..." className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-description-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nettside</label>
                <input type="text" value={profileData.website_url} onChange={(e) => setProfileData({...profileData, website_url: e.target.value})} placeholder="https://www.eksempel.no" className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-website-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Logo URL (valgfritt)</label>
                <input type="text" value={profileData.logo_url} onChange={(e) => setProfileData({...profileData, logo_url: e.target.value})} placeholder="https://..." className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-logo-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Offentlig telefon</label>
                <input type="text" value={profileData.public_phone} onChange={(e) => setProfileData({...profileData, public_phone: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-phone-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Offentlig e-post</label>
                <input type="text" value={profileData.public_email} onChange={(e) => setProfileData({...profileData, public_email: e.target.value})} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="profile-email-input" />
              </div>
            </div>
            {profileMsg && <p className={`text-sm mt-3 ${profileMsg.includes('oppdatert') ? 'text-emerald-600' : 'text-red-600'}`}>{profileMsg}</p>}
            <div className="flex gap-3 mt-4">
              <button onClick={saveProfile} className="flex-1 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-2 font-medium transition-colors" data-testid="save-profile-btn">Lagre profil</button>
              <button onClick={() => setProfileModal(null)} className="flex-1 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 font-medium transition-colors" data-testid="cancel-profile-btn">Avbryt</button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="delete-modal">
            <h3 className="text-lg font-semibold text-red-700 mb-1">Slett forhandler</h3>
            <p className="text-sm text-stone-500 mb-4">Denne handlingen kan ikke angres.</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-800">
              Du er i ferd med å slette <strong>{deleteModal.company_name}</strong> ({deleteModal.username}).<br/>
              Eventuelle sub-brukere slettes også. Kit-esker må flyttes til en annen forhandler først.
            </div>
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={deleteWarranties} onChange={(e) => setDeleteWarranties(e.target.checked)} className="mt-1" data-testid="delete-warranties-checkbox" />
              <span className="text-sm text-stone-700">Slett også alle garantiregistreringer knyttet til denne forhandleren</span>
            </label>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Skriv <span className="font-mono bg-stone-100 px-1 rounded">{deleteModal.company_name}</span> for å bekrefte</label>
              <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="delete-confirm-input" />
            </div>
            {deleteMsg && <p className={`text-sm mt-3 ${deleteMsg.includes('slettet') ? 'text-emerald-600' : 'text-red-600'}`}>{deleteMsg}</p>}
            <div className="flex gap-3 mt-4">
              <button onClick={confirmDelete} disabled={deleting || deleteConfirm !== deleteModal.company_name} className="flex-1 bg-red-700 text-white hover:bg-red-800 disabled:bg-stone-300 disabled:cursor-not-allowed rounded-lg px-4 py-2 font-medium transition-colors" data-testid="confirm-delete-btn">
                {deleting ? 'Sletter...' : 'Slett forhandler'}
              </button>
              <button onClick={() => setDeleteModal(null)} className="flex-1 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 font-medium transition-colors" data-testid="cancel-delete-btn">Avbryt</button>
            </div>
          </div>
        </div>
      )}
      {timelineModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setTimelineModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto" data-testid="activity-timeline-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-stone-900 mb-1">Endringslogg</h3>
            <p className="text-sm text-stone-500 mb-4">{timelineModal.dealer.company_name}</p>
            {timelineModal.loading ? (
              <p className="text-sm text-stone-500">Laster...</p>
            ) : timelineModal.entries.length === 0 ? (
              <p className="text-sm text-stone-500">Ingen endringer registrert ennå.</p>
            ) : (
              <ol className="relative border-l-2 border-stone-200 ml-2 space-y-4">
                {timelineModal.entries.map((e, i) => {
                  let dateStr = e.at;
                  try { dateStr = new Date(e.at).toLocaleString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { /* fallback */ }
                  return (
                    <li key={i} className="ml-4" data-testid={`activity-entry-${i}`}>
                      <div className="absolute -left-[7px] w-3 h-3 bg-emerald-800 rounded-full mt-1.5" />
                      <div className="text-sm text-stone-900 font-medium">{e.action}</div>
                      <div className="text-xs text-stone-500">{dateStr} · av {e.by || 'admin'}</div>
                      {e.details && <div className="text-xs text-stone-500 mt-0.5 italic">{e.details}</div>}
                    </li>
                  );
                })}
              </ol>
            )}
            <button onClick={() => setTimelineModal(null)} className="w-full mt-6 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 font-medium transition-colors" data-testid="close-timeline-btn">Lukk</button>
          </div>
        </div>
      )}

      {undoState && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-stone-900 text-white rounded-xl shadow-xl px-5 py-3 flex items-center gap-4" data-testid="undo-delete-toast">
          <span className="text-sm">
            <strong>{undoState.dealerName}</strong> slettet.
            <span className="text-stone-400 ml-2 text-xs">Angre innen {undoState.secondsLeft} sek</span>
          </span>
          <button onClick={restoreDealer} className="bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors" data-testid="undo-delete-btn">
            Angre
          </button>
          <button onClick={() => setUndoState(null)} className="text-stone-400 hover:text-white text-lg leading-none" data-testid="dismiss-undo-btn" aria-label="Lukk">×</button>
        </div>
      )}
    </div>
  );
}
